import {
  Breadcrumb,
  Button, Card, ErrorMessage, Label, WarningCallout,
} from 'nhsuk-react-components';
import {
  useEffect,
  useRef,
  useState,
} from 'react';
import { useLocation, useParams } from 'react-router-dom';
import saveAs from 'file-saver';
import ApiErrorDetails from 'components/ApiErrorDetails/index.tsx';
import Confirm from 'components/Confirm/index.tsx';
import IterationPageHeader from 'components/IterationPageHeader/index.tsx';
import SummaryReportsLink from 'components/SummaryReportsLink/index.tsx';
import IterationDetailTable from 'components/SummaryTable/IterationDetail/IterationDetailTable.tsx';
import RequestSummaryReportButton from 'components/SummaryReportsActions/Request/index.tsx';
import RulesTable from 'components/SummaryTable/Rules/RulesTable.tsx';
import useCampaign from 'hooks/useCampaign.ts';
import useCampaignNavigate from 'hooks/useCampaignNavigate.ts';
import { useCampaignStatus } from 'hooks/useCampaignStatus.ts';
import useIterationLock from 'hooks/useIterationLock.ts';
import useUser, { UserData } from 'hooks/useUser.ts';
import useUserAuthorisation from 'hooks/useUserAuthorisation';
import { ITERATION_STATUS } from 'schemas/CampaignApiDetails.schema.ts';
import { IterationRule } from 'schemas/IterationRule.schema.ts';
import featureFlags from 'services/featureFlags.ts';
import {
  canArchive,
  canRejectApprove,
  canRevertToDraft,
  canSendForReview,
  isRevertToDraftGreenColour,
} from 'utils/iterationStatusUtils.ts';
import { getRulesDisplay, updateRules } from 'utils/ruleUtils.ts';
import './IterationSummaryPage.scss';
import useFetchIterationExport from 'queries/fetch/fetchIterationExport.ts';
import useUpdateIteration from 'queries/mutate/updateIteration.ts';
import {
  getIterationById,
  getIterationDetailsById,
  hasRoutingPlanErrors,
} from 'utils/iterationUtils.ts';
import { IterationDetailsFormValues } from 'schemas/Iteration.schema.ts';
import { IterationDetails } from 'schemas/CampaignConfig.schema.ts';
import useUpdateIterationStatus from 'queries/mutate/updateIterationStatus.ts';
import useFetchSummaryReport from 'queries/fetch/fetchSummaryReport.ts';
import { getIsCurrentSummaryReport } from 'utils/summaryReports';
import useFetchTeamDefaultRules from 'queries/fetch/fetchTeamDefaultRules';
import { getRulesContent } from 'utils/contentUtils';
import {
  API_EXPORT_ITERATION_ERROR,
  API_UPDATE_ITERATION_ERROR,
  API_UPDATE_RULES_ERROR,
} from 'constants/errorMessages';
import { ApiError } from 'schemas/ApiError.schema';
import { IIterationLevelParams } from 'utils/parameterUtils';
import useFetchSpecificCampaign from 'queries/fetch/fetchSpecificCampaign';
import useFetchTeamRoutingPlans from 'queries/fetch/fetchTeamRoutingPlans';
import { getOldRoutingPlans, getOldRoutingRules } from 'utils/routingPlansUtils';
import useFetchTemplateForCampaign from 'queries/fetch/fetchTemplateForCampaign.ts';

const getCanUpdateIteration = (
  canUpdateCampaign: () => boolean,
  iterationDetails: IterationDetails,
  user: UserData,
) => {
  if (
    iterationDetails?.status
    && iterationDetails.status.toLowerCase() !== ITERATION_STATUS.DRAFT
  ) {
    return false;
  }

  const isLocked = Boolean(iterationDetails?.locked_by?.id);
  const isLockedByMe = user.userId === iterationDetails?.locked_by?.id;

  return canUpdateCampaign() && (!isLocked || isLockedByMe);
};

function IterationSummaryPage() {
  const {
    notifyRoutingPlansEnabled,
  } = featureFlags;

  const user = useUser();
  const { campaign } = useCampaign();
  const { canUpdateCampaign, canApproveCampaign } = useUserAuthorisation();
  const { teamId, campaignId, iterationId } = useParams() as IIterationLevelParams;
  const {
    lockIteration,
    lockIterationIfRequired,
    unlockIteration,
    error: lockError,
    isPending: lockIsPending,
  } = useIterationLock({ teamId, campaignId, iterationId });
  const updateIteration = useUpdateIteration();
  const updateIterationStatus = useUpdateIterationStatus();
  const fetchIterationExport = useFetchIterationExport({
    campaignId: campaignId!,
    iterationId: iterationId!,
    teamId: teamId!,
  });
  const fetchSummaryReports = useFetchSummaryReport({
    teamId: teamId!,
    campaignId: campaignId!,
    iterationId: iterationId!,
  });
  const summaryReports = fetchSummaryReports.data;
  const { hasIterationErrors } = useCampaignStatus(campaign);
  const { navigateToCampaign } = useCampaignNavigate();
  const [showExportErrorMessage, setShowExportErrorMessage] = useState(false);
  const [showExportMessage, setShowExportMessage] = useState(false);
  const [showSendForReviewErrorMessage, setShowSendForReviewErrorMessage] = useState(false);
  const [showRevertToDraftDialog, setShowRevertToDraftDialog] = useState(false);
  const initialRenderRef = useRef(true);
  const errorRef = useRef<HTMLDivElement | null>(null);
  const [statusUpdateError, setStatusUpdateError] = useState<ApiError | null>(null);
  const [error, setError] = useState<ApiError | null>(null);

  const { search } = useLocation();
  const searchParams = new URLSearchParams(search);
  const [openRules, setOpenRules] = useState(searchParams.get('openRules') || '');
  const [isUpdatingStatus, setIsUpdatingStatus] = useState(false);

  useEffect(() => {
    const params = new URLSearchParams(search);
    setOpenRules(params.get('openRules') || '');
  }, [search]);

  const currentIteration = getIterationById(campaign, iterationId!);
  const iterationDetails = getIterationDetailsById(campaign, iterationId!);

  const latestReport = fetchSummaryReports.data?.find((report) => currentIteration && getIsCurrentSummaryReport(report, currentIteration));
  const latestReportRunTime = latestReport?.run_time;

  const fetchSpecificCampaign = useFetchSpecificCampaign({ teamId, campaignId });

  const fetchedTeamDefaultRules = useFetchTeamDefaultRules({ teamId: teamId! });
  const teamDefaultRules = fetchedTeamDefaultRules.data;

  const { data: teamRoutingPlans } = useFetchTeamRoutingPlans({ teamId, enabled: notifyRoutingPlansEnabled });

  const oldRoutingPlans = getOldRoutingPlans(teamRoutingPlans, currentIteration?.DefaultCommsRouting);
  const oldRoutingRules = getOldRoutingRules(teamRoutingPlans, currentIteration?.IterationRules);

  const { data: campaignTemplate } = useFetchTemplateForCampaign({
    teamId,
    campaignId,
    isEnabled: Boolean(campaign.CampaignConfig.Template),
  });

  useEffect(() => {
    if (fetchSpecificCampaign.isSuccess && initialRenderRef.current) {
      initialRenderRef.current = false;
      lockIterationIfRequired();
    }
  }, [fetchSpecificCampaign.isSuccess, lockIterationIfRequired]);

  useEffect(() => {
    if (!fetchSpecificCampaign.isFetched) {
      return;
    }

    if (!currentIteration) {
      navigateToCampaign('');
    }
  }, [currentIteration, fetchSpecificCampaign.isFetched, navigateToCampaign]);

  if (!currentIteration) {
    return null;
  }

  const canUpdateIteration = getCanUpdateIteration(canUpdateCampaign, iterationDetails!, user);

  const lockedByAnotherUserInDraft = !!(
    iterationDetails?.locked_by?.id
    && iterationDetails.locked_by?.id !== user.userId
    && iterationDetails.status === ITERATION_STATUS.DRAFT
  );

  const handleExport = async () => {
    setShowSendForReviewErrorMessage(false);

    if (hasIterationErrors(currentIteration)) {
      const hasRoutingErrors = hasRoutingPlanErrors(currentIteration);

      if (hasRoutingErrors) {
        setOpenRules('R');
      }

      setShowExportErrorMessage(true);

      setTimeout(() => {
        errorRef.current?.scrollIntoView();
      }, 0);

      return;
    }

    try {
      const response = await fetchIterationExport.refetch();
      const data = JSON.stringify(response.data?.config, null, 4);
      const blob = new Blob([data], { type: 'application/json' });
      const { Name, Iterations } = response.data?.config.CampaignConfig ?? {};
      saveAs(blob, `${Name}-${Iterations?.[0]?.Name}.json`);
      setShowExportMessage(true);
    } catch {
      setError({ message: API_EXPORT_ITERATION_ERROR });
    }
  };

  const handleApproveIteration = async () => {
    setStatusUpdateError(null);
    setIsUpdatingStatus(true);
    try {
      await updateIterationStatus.mutateAsync({
        teamId: teamId!,
        campaignId: campaignId!,
        iterationId: iterationId!,
        status: ITERATION_STATUS.APPROVED,
      });
    } catch {
      setStatusUpdateError({ message: API_UPDATE_ITERATION_ERROR });
    }

    setIsUpdatingStatus(false);
  };

  const handleRejectIteration = async () => {
    setStatusUpdateError(null);
    setIsUpdatingStatus(true);
    try {
      await updateIterationStatus.mutateAsync({
        teamId: teamId!,
        campaignId: campaignId!,
        iterationId: iterationId!,
        status: ITERATION_STATUS.REJECTED,
      });
    } catch {
      setStatusUpdateError({ message: API_UPDATE_ITERATION_ERROR });
    }

    setIsUpdatingStatus(false);
  };

  const handleRevertToDraft = () => {
    setShowRevertToDraftDialog(true);
  };

  const handleRevertToDraftNo = () => {
    setShowRevertToDraftDialog(false);
  };

  const handleRevertToDraftYes = async () => {
    setShowRevertToDraftDialog(false);
    setStatusUpdateError(null);
    setIsUpdatingStatus(true);

    try {
      await updateIterationStatus.mutateAsync({
        teamId: teamId!,
        campaignId: campaignId!,
        iterationId: iterationId!,
        status: ITERATION_STATUS.DRAFT,
      });
    } catch {
      setStatusUpdateError({ message: API_UPDATE_ITERATION_ERROR });
    }

    setIsUpdatingStatus(false);
  };

  const handleSendForReview = async () => {
    if (!campaign?.CampaignConfig?.ID || !iterationId) return;

    setShowExportErrorMessage(false);

    if (hasIterationErrors(currentIteration)) {
      const hasRoutingErrors = hasRoutingPlanErrors(currentIteration);

      if (hasRoutingErrors) {
        setOpenRules('R');
      }

      setShowSendForReviewErrorMessage(true);

      setTimeout(() => {
        errorRef.current?.scrollIntoView();
      }, 0);

      return;
    }

    if (oldRoutingPlans.length > 0 || oldRoutingRules.length > 0) {
      navigateToCampaign(`iteration/${iterationId}/message-plans-updated`);
      return;
    }

    navigateToCampaign(`iteration/${iterationId}/reviewers`);
  };

  const handleRulesSave = async (rules: IterationRule[], ruleType: 'F' | 'S' | 'R') => {
    const payload = {
      IterationID: iterationId as string,
      Rules: rules,
      Type: ruleType,
    };

    const updatedCampaign = updateRules(campaign, payload);

    try {
      setIsUpdatingStatus(true);
      await updateIteration.mutateAsync({
        campaignId: campaignId!,
        iteration: getIterationById(updatedCampaign, iterationId!) as IterationDetailsFormValues,
        iterationId: iterationId!,
        teamId: teamId!,
      });

      await fetchSpecificCampaign.refetch();
    } catch {
      setError({ message: API_UPDATE_RULES_ERROR });
    }

    setIsUpdatingStatus(false);
  };

  const handleUnlockIteration = async () => {
    await unlockIteration();
    await lockIteration();
  };

  const handleSelectRunType = () => {
    navigateToCampaign(`iteration/${iterationId}/select-run-type`);
  };

  const handleArchiveIteration = () => {
    navigateToCampaign(`iteration/${iterationId}/archive-iteration-confirmation`);
  };

  const showApproveRejectButtons = () => canUpdateCampaign() || canApproveCampaign();

  const allowRejectApprove = canRejectApprove(
    canApproveCampaign(),
    iterationDetails,
    user.userId,
  );

  const allowRevertToDraft = canRevertToDraft(
    canUpdateCampaign(),
    iterationDetails,
    user.userId,
  );

  const allowSendForReview = canSendForReview(
    canUpdateCampaign(),
    iterationDetails,
    user.userId,
  );

  const allowArchive = canArchive(
    canUpdateCampaign(),
    iterationDetails,
    user.userId,
  );

  const canRequestSummaryReport = iterationDetails?.status === ITERATION_STATUS.APPROVED
    && canUpdateCampaign()
    && user.roles.includes(`${teamId}:release`);

  const useGreenColour = isRevertToDraftGreenColour(iterationDetails);

  const rulesContent = getRulesContent(campaign.CampaignConfig?.Type);

  return (
    <div className="nhsuk-u-margin-bottom-6">
      <div className="nhsuk-u-margin-bottom-6">
        <Breadcrumb>
          <Breadcrumb.Item href="/">
            Home
          </Breadcrumb.Item>
          <Breadcrumb.Item href="/campaigns">
            Manage campaigns
          </Breadcrumb.Item>
          <Breadcrumb.Item href={`/${teamId}/campaign/${campaignId}/summary`}>
            Campaign summary
          </Breadcrumb.Item>
          <Breadcrumb.Item>
            <span aria-current="page">Iteration summary</span>
          </Breadcrumb.Item>
        </Breadcrumb>
      </div>

      {lockedByAnotherUserInDraft && (
        <WarningCallout>
          <WarningCallout.Heading>This iteration is locked</WarningCallout.Heading>
          <p>
            This iteration is currently locked by
            {' '}
            <b>{iterationDetails?.locked_by?.name}</b>
            .
          </p>

            {!canUpdateCampaign() && (
              <p>
                Some details might be out of date until they save their changes.
              </p>
            )}
            {canUpdateCampaign() && (
              <p>
                If you unlock it and make changes, their work could be overwritten. We recommend checking with them
                before you continue.
                To unlock the iteration, click the button at the bottom of this page.
              </p>
            )}
        </WarningCallout>
      )}

      <IterationPageHeader
        iteration={currentIteration}
        title="Set iteration details"
        showTitleAndLink={false}
        showCopyLink
        actualRunTime={latestReportRunTime}
      />

      {
        iterationDetails?.status === ITERATION_STATUS.IN_REVIEW
        && !(iterationDetails.reviewers ?? []).includes(user.userId)
        && user.roles.includes(`${teamId}:approve`)
        && (
          <WarningCallout>
            <WarningCallout.Heading>You’re not a reviewer for this iteration</WarningCallout.Heading>
            <p>
              You can still view it, but you do not have permission to approve or reject.
            </p>
          </WarningCallout>
        )
      }

      <Card>
        <>
          <div className="nhsuk-grid-row nhsuk-u-margin-bottom-3">
            <div className="nhsuk-grid-column-one-half">
              <Card.Heading>Iteration details</Card.Heading>
            </div>
          </div>
          <IterationDetailTable
            iteration={currentIteration}
            canUpdate={canUpdateIteration}
            teamRoutingPlans={teamRoutingPlans}
            template={campaignTemplate}
          />
        </>
      </Card>
      {currentIteration.IterationRules && (
        <Card>
          <div className="nhsuk-u-margin-bottom-5">
            <div>
              <h2>Iteration rules</h2>
              {currentIteration.IterationRules.length === 0 && (
                <p>No rules have been created for the iteration.</p>
              )}
              <RulesTable
                rules={getRulesDisplay(currentIteration, currentIteration.IterationRules)}
                teamDefaultRules={getRulesDisplay(currentIteration, teamDefaultRules ?? [])}
                iterationId={iterationId as string}
                canUpdateFilterRules={canUpdateIteration && campaignTemplate?.['IterationDetails.IterationRules.filters.input.display'] !== 'hidden'}
                canUpdateSuppressionRules={canUpdateIteration && campaignTemplate?.['IterationDetails.IterationRules.suppressions.input.display'] !== 'hidden'}
                canUpdateRoutingRules={canUpdateIteration && campaignTemplate?.['IterationDetails.IterationRules.routing.input.display'] !== 'hidden'}
                handleRulesSave={handleRulesSave}
                openRules={openRules}
                rulesContent={rulesContent}
                teamRoutingPlans={teamRoutingPlans}
                campaignTemplate={campaignTemplate}
              />
              {showExportMessage && (
                <Label className="nhsuk-u-font-weight-bold nhsuk-u-margin-bottom-4">
                  The iteration and campaign files are now exported. Check your
                  download folder to find them.
                </Label>
              )}
              <div>
                <div className="nhsuk-u-margin-top-3 nhsuk-u-text-align-right" ref={errorRef} tabIndex={-1} aria-live="assertive">
                  <div className={showSendForReviewErrorMessage ? 'missing-details-error' : undefined}>
                    {showSendForReviewErrorMessage && (
                      <div className="nhsuk-u-margin-right-3">
                        <ErrorMessage>
                          Complete the missing details for this iteration before sending it for review.
                        </ErrorMessage>
                      </div>
                    )}

                  </div>
                  <div className={showExportErrorMessage ? 'missing-details-error' : undefined}>
                    {showExportErrorMessage && (
                      <div className="nhsuk-u-margin-top-4 nhsuk-u-margin-right-3">
                        <ErrorMessage>
                          Complete the missing details for this iteration before exporting it.
                        </ErrorMessage>
                      </div>
                    )}
                    <div className="nhsuk-u-margin-top-4" />
                    {
                        user.roles.includes(`${teamId}:admin`) && (
                          <Button
                            secondary
                            onClick={handleExport}
                            className="nhsuk-u-margin-right-3 nhsuk-u-margin-bottom-4"
                            disabled={fetchIterationExport.isFetching}
                          >
                            {fetchIterationExport.isFetching ? 'Updating...' : 'Export iteration and campaign'}
                          </Button>
                        )
                    }
                  </div>
                </div>
                <div
                  className="nhsuk-u-margin-right-3 nhsuk-u-margin-bottom-1"
                  style={{
                    display: 'flex',
                    flexDirection: 'column',
                    alignItems: 'flex-end',
                  }}
                >
                  {summaryReports && summaryReports.length > 0 && (
                    <p className="nhsuk-u-margin-top-1">
                      <SummaryReportsLink summaryReports={summaryReports} />
                    </p>
                  )}
                </div>
              </div>
            </div>
          </div>
        </Card>
      )}

      {showApproveRejectButtons() && (
        <div className="cta-buttons">
            {allowRejectApprove && (
              <>
                <Button onClick={handleRejectIteration} warning disabled={isUpdatingStatus}>
                  {isUpdatingStatus ? 'Updating...' : 'Reject iteration'}
                </Button>
                <Button
                  onClick={handleApproveIteration}
                  disabled={isUpdatingStatus}
                >
                  {isUpdatingStatus ? 'Updating...' : 'Approve iteration'}
                </Button>
              </>
            )}
            {allowRevertToDraft
            && iterationDetails?.status !== ITERATION_STATUS.REJECTED
            && iterationDetails?.status !== ITERATION_STATUS.REPORT_REJECTED && (
              <Button
                onClick={handleRevertToDraft}
                warning={!useGreenColour}
                disabled={isUpdatingStatus}
              >
                {isUpdatingStatus ? 'Updating...' : 'Revert to draft'}
              </Button>
            )}

            {allowArchive && (
              <Button onClick={handleArchiveIteration} warning>
                Archive iteration
              </Button>
            )}

            {iterationDetails?.status !== ITERATION_STATUS.IN_REVIEW && (
              <>
                {lockedByAnotherUserInDraft && (
                  <Button warning onClick={handleUnlockIteration} disabled={lockIsPending}>
                    {lockIsPending ? 'Updating...' : 'Unlock iteration'}
                  </Button>
                )}

                {featureFlags.dryRunEnabled && canRequestSummaryReport && (
                  <Button onClick={handleSelectRunType}>
                    Request summary report
                  </Button>
                )}

                {!featureFlags.dryRunEnabled && (
                  <RequestSummaryReportButton
                    teamId={teamId as string}
                    iterationId={iterationId as string}
                    campaignId={campaign.CampaignConfig?.ID as string}
                    iterationStatus={iterationDetails?.status}
                    hasUpdateRole={canUpdateCampaign()}
                  />
                )}
              </>
            )}

            {allowRevertToDraft
            && (iterationDetails?.status === ITERATION_STATUS.REJECTED
              || iterationDetails?.status
                === ITERATION_STATUS.REPORT_REJECTED) && (
                <Button
                  onClick={handleRevertToDraft}
                  disabled={isUpdatingStatus}
                >
                  {isUpdatingStatus ? 'Updating...' : 'Revert to draft'}
                </Button>
            )}

            {allowSendForReview && (
              <Button
                onClick={handleSendForReview}
                className="align-right"
              >
                Send for review
              </Button>
            )}
        </div>
      )}

      <ApiErrorDetails apiError={error || statusUpdateError || fetchIterationExport.error || fetchSummaryReports.error || updateIteration.error || updateIterationStatus.error || lockError} />

      {showRevertToDraftDialog && (
        <Confirm
          title="Revert to draft?"
          message="This will change the iteration back to draft so you can make changes. You’ll need to complete all the review steps again, including sending it for a run."
          yesText="Yes, revert to draft"
          noText="No, return to summary"
          onYes={handleRevertToDraftYes}
          onNo={handleRevertToDraftNo}
        />
      )}
    </div>
  );
}

export default IterationSummaryPage;
